// --------------------------------------------------------------
// Created On: 2026-10-23
// Author: Zachary Thomas
//
// Last Modified: 2026-11-03
// Modified By: Zachary Thomas
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import Modal from "src/components/Modal/Modal";
import ModalHeader from "src/components/ModalHeader/ModalHeader";
import ModalBody from "src/components/ModalBody/ModalBody";
import ModalFooter from "src/components/ModalFooter/ModalFooter";
import RemoteOperationModalContent from "./RemoteOperationModalContent/RemoteOperationModalContent";
import styles from "./RemoteOperationModal.module.scss";

// Modal for remotely operating the asset associated with a system control component.
export default function RemoteOperationModal(props: Props): Component {
  const [position, setPosition] = useState<"left" | "center" | "right">("center");

  // Returns the class names for the dialog, which shifts to the side of the screen based on its position.
  function getDialogClassName(): string {
    if (position === "left") {
      return `${styles.dialog} ${styles.left}`;
    } else if (position === "right") {
      return `${styles.dialog} ${styles.right}`;
    } else {
      return styles.dialog;
    }
  }

  // Moves the dialog toward the left of the screen, or back to the center if it is on the right.
  function moveLeft(): void {
    if (position === "right") {
      setPosition("center");
    } else {
      setPosition("left");
    }
  }

  // Moves the dialog toward the right of the screen, or back to the center if it is on the left.
  function moveRight(): void {
    if (position === "left") {
      setPosition("center");
    } else {
      setPosition("right");
    }
  }

  return (
    <div data-test="remote-operation-modal">
      <Modal
        show={props.showModal}
        onHide={() => props.onClose()}
        dialogClassName={getDialogClassName()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        animation
        centered
      >
        {position !== "left" && (
          <button
            data-test="remote-operation-modal-move-left-button"
            type="button"
            className={`${styles.arrow} ${styles.arrowLeft}`}
            aria-label="Move left"
            onClick={() => moveLeft()}
          >
            <i className="fa fa-fw fa-chevron-left" />
          </button>
        )}
        {position !== "right" && (
          <button
            data-test="remote-operation-modal-move-right-button"
            type="button"
            className={`${styles.arrow} ${styles.arrowRight}`}
            aria-label="Move right"
            onClick={() => moveRight()}
          >
            <i className="fa fa-fw fa-chevron-right" />
          </button>
        )}
        <ModalHeader>
          <h5>Remote Operation</h5>
        </ModalHeader>
        <ModalBody>{props.assetId !== null && <RemoteOperationModalContent assetId={props.assetId} />}</ModalBody>
        <ModalFooter>
          <button
            data-test="remote-operation-modal-close-button"
            type="button"
            className="btn btn-secondary"
            onClick={() => props.onClose()}
          >
            Close
          </button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

interface Props {
  showModal: boolean;
  assetId: number | null;
  onClose: () => void;
}
